import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Users, CreditCard, Check, Clock, ArrowUpRight, Loader2, X, AlertTriangle } from "lucide-react";
import { toast } from "./ui/sonner";
import { useAppearance } from "../contexts/AppearanceContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssinaturaModal({ open, onOpenChange, onSubscriptionChange }) {
  const navigate = useNavigate();
  const appearance = useAppearance();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API}/stripe/my-subscription`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    fetchData();
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const plan = data?.subscription_plan;
  const pricing = data?.pricing;
  const isActive = data?.subscription_status === "active";
  const isCancelling = data?.subscription_status === "cancelling" || data?.cancel_at_period_end;
  const maxUsers = plan?.total_users || pricing?.included_users || 1;
  const activeUsers = data?.active_users || 0;
  const usagePercent = Math.min(100, Math.round((activeUsers / maxUsers) * 100));

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/stripe/cancel`, {}, { withCredentials: true });
      toast.success("Cancelamento agendado para o final do período.");
      setShowCancelConfirm(false);
      fetchData();
      onSubscriptionChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cancelar");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await axios.post(`${API}/stripe/reactivate`, {}, { withCredentials: true });
      toast.success("Assinatura reativada com sucesso!");
      fetchData();
      onSubscriptionChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reativar");
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5" style={{ color: appearance?.colors?.["500"] || "#3b82f6" }} />
            Minha Assinatura
          </h2>
          <button onClick={() => { onOpenChange(false); setShowCancelConfirm(false); }} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Status badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">Status</span>
                </div>
                <Badge className={`text-[11px] border-0 ${isActive && !isCancelling ? "bg-emerald-100 text-emerald-700" : isCancelling ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>
                  {isActive && !isCancelling ? "Ativa" : isCancelling ? "Cancelamento agendado" : data?.subscription_status === "none" ? "Sem plano" : "Trial"}
                </Badge>
              </div>

              {/* Plan details */}
              {(isActive || isCancelling) && plan && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Plano</span>
                    <span className="text-sm font-bold text-slate-800">Pipe4.me</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Valor mensal</span>
                    <span className="text-sm font-bold text-slate-800">R${plan.amount?.toFixed(2)}/mês</span>
                  </div>
                  {data?.subscription_activated_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Ativo desde</span>
                      <span className="text-sm text-slate-700">{new Date(data.subscription_activated_at).toLocaleDateString("pt-BR")}</span>
                    </div>
                  )}
                  {data?.current_period_end && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">{isCancelling ? "Acesso até" : "Próxima cobrança"}</span>
                      <span className="text-sm text-slate-700">{new Date(data.current_period_end).toLocaleDateString("pt-BR")}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Cancelling warning */}
              {isCancelling && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-amber-800 font-medium">Cancelamento agendado</p>
                    <p className="text-[11px] text-amber-600 mt-0.5">
                      {data?.current_period_end
                        ? <>Acesso disponível até <span className="font-semibold">{new Date(data.current_period_end).toLocaleDateString("pt-BR")}</span>. Pode reativar a qualquer momento.</>
                        : "Você ainda tem acesso até o fim do período pago. Pode reativar a qualquer momento."
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* User usage */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">Usuários</span>
                  </div>
                  <span className="text-sm text-slate-600">
                    <span className="font-bold">{activeUsers}</span> de <span className="font-bold">{maxUsers}</span>
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${usagePercent >= 90 ? "bg-rose-500" : usagePercent >= 70 ? "bg-amber-500" : "bg-blue-500"}`}
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
                {activeUsers >= maxUsers && (
                  <p className="text-[11px] text-rose-500 font-medium">Limite atingido. Adicione mais usuários ao plano.</p>
                )}
              </div>

              {/* Pricing info */}
              {pricing && (
                <div className="text-[11px] text-slate-400 bg-slate-50 rounded-lg px-3 py-2">
                  Plano base: R${pricing.base_price?.toFixed(2)}/mês ({pricing.included_users} usuário incluso) · Adicional: R${pricing.extra_user_price?.toFixed(2)}/usuário
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2">
                {/* Upgrade / Subscribe — hide when cancelling */}
                {!isCancelling && (
                  <Button
                    data-testid="subscription-upgrade-btn"
                    onClick={() => { onOpenChange(false); navigate("/checkout"); }}
                    className="w-full h-10 text-white font-medium rounded-xl"
                    style={{ backgroundColor: "#3B82F6" }}
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    {isActive ? "Alterar plano" : "Contratar plano"}
                  </Button>
                )}

                {/* Reactivate */}
                {isCancelling && (
                  <Button
                    data-testid="subscription-reactivate-btn"
                    onClick={handleReactivate}
                    disabled={reactivating}
                    className="w-full h-10 text-white font-medium rounded-xl bg-emerald-600 hover:bg-emerald-700"
                  >
                    {reactivating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Reativar assinatura
                  </Button>
                )}

                {/* Cancel */}
                {isActive && !isCancelling && (
                  <button
                    data-testid="subscription-cancel-btn"
                    onClick={() => setShowCancelConfirm(true)}
                    className="w-full text-center text-xs text-slate-400 hover:text-rose-500 transition-colors py-2"
                  >
                    Cancelar assinatura
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Cancel confirmation modal (separate overlay) */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-6 h-6 text-rose-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Cancelar assinatura?</h3>
              <p className="text-sm text-slate-500 mb-6">
                {data?.current_period_end
                  ? <>Você continuará com acesso até <span className="font-semibold text-slate-700">{new Date(data.current_period_end).toLocaleDateString("pt-BR")}</span>. Após essa data, o sistema será bloqueado.</>
                  : "Você continuará com acesso até o final do período já pago. Após isso, o sistema será bloqueado."
                }
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full h-10 text-sm font-medium"
                  onClick={() => setShowCancelConfirm(false)}
                >
                  Manter meu plano
                </Button>
                <Button
                  className="w-full h-10 text-sm font-medium bg-rose-600 hover:bg-rose-700 text-white"
                  disabled={cancelling}
                  onClick={handleCancel}
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Confirmar cancelamento
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
